import { useRef } from 'react';

export const BLEED = 12;

let _gidc = 0;

// clear-plastic / cool-grey palette
const CLEAR = '#e9edf2';
const GREY = '#aab2bd';
const GREY_DK = '#6b7280';
const TEXT = '#c9c9d2';
const INK = '#0a0a0c';

// "JEWEL CASE" frame — a translucent clear-acrylic optical-disc case (generic
// recordable-disc VIBE, no brand marks). Layered cool-white/grey rim strokes with a
// faint inner bevel and bright specular corner glints + tiny prism dispersion ticks; a
// left SPINE column (generic DISC label box, vertical "DIGITAL DISC" / "DIGITAL
// SOUND", a globe glyph, a dot row, a "D" chip and "INSERT THIS END" + triangle);
// a portion of an iridescent rainbow disc with a metallic concentric hub peeking
// from the right edge (upper-mid, clear of the Going pill) with a generic spec
// sticker; registration "+" marks in each corner; "OPEN" + triangle bottom-center;
// and a very faint dotted J-card insert rectangle in the centre. All decoration
// hugs the border / corners / bleed so the card text stays readable.
export default function JewelCaseFrame({ w, h, radius = 12, accent }) {
  const idRef = useRef(null);
  if (idRef.current === null) idRef.current = `jc${++_gidc}_`;
  const g = idRef.current;

  const W = w + BLEED * 2;
  const H = h + BLEED * 2;
  const x = BLEED;
  const y = BLEED;
  const r = radius + 1;

  // card-box edges in SVG coords
  const left = x + 1;
  const right = x + w - 1;
  const top = y + 1;
  const bottom = y + h - 1;
  const cx = x + w / 2;

  // iridescent disc: centred just past the right edge, upper-mid
  const dcx = x + w + 16;
  const dcy = y + h * 0.4;
  const dr = Math.min(54, h * 0.27);

  return (
    <div style={{ position: 'absolute', top: -BLEED, left: -BLEED, width: W, height: H, pointerEvents: 'none' }}>
      <svg width={W} height={H} xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id={`${g}iri`} x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor="#ff7ab0" />
            <stop offset="0.25" stopColor="#ffd27a" />
            <stop offset="0.5" stopColor="#8ef0c0" />
            <stop offset="0.75" stopColor="#7ab8ff" />
            <stop offset="1" stopColor="#c79bff" />
          </linearGradient>
          <radialGradient id={`${g}hub`} cx="0.5" cy="0.5" r="0.5">
            <stop offset="0" stopColor={CLEAR} />
            <stop offset="0.5" stopColor={GREY} />
            <stop offset="1" stopColor={GREY_DK} />
          </radialGradient>
        </defs>

        {/* ===== OUTER clear-acrylic case rim: layered translucent strokes ===== */}
        <rect x={x} y={y} width={w} height={h} rx={r} ry={r} fill="none" stroke={GREY_DK} strokeWidth={3} strokeOpacity={0.5} />
        <rect x={x} y={y} width={w} height={h} rx={r} ry={r} fill="none" stroke={GREY} strokeWidth={1.6} strokeOpacity={0.7} />
        <rect x={x - 2} y={y - 2} width={w + 4} height={h + 4} rx={r + 1} ry={r + 1} fill="none" stroke={CLEAR} strokeWidth={1} strokeOpacity={0.5} />
        {/* faint inner bevel line */}
        <rect x={x + 4} y={y + 4} width={w - 8} height={h - 8} rx={Math.max(2, r - 3)} ry={Math.max(2, r - 3)} fill="none" stroke={CLEAR} strokeWidth={0.6} strokeOpacity={0.3} />

        {/* ===== specular corner glints (bright plastic shine streaks) ===== */}
        <g stroke="#ffffff" strokeLinecap="round">
          <path d={`M ${left + 13} ${y - 1} L ${left + 33} ${y - 1} M ${x - 1} ${top + 13} L ${x - 1} ${top + 33}`} strokeWidth={2} strokeOpacity={0.95} />
          <path d={`M ${left + 17} ${y + 5} L ${left + 27} ${y + 5}`} strokeWidth={1} strokeOpacity={0.7} />
          <path d={`M ${right + 1} ${top + 13} L ${right + 1} ${top + 37} M ${right - 13} ${y - 1} L ${right - 27} ${y - 1}`} strokeWidth={2} strokeOpacity={0.9} />
          <path d={`M ${x - 1} ${bottom - 13} L ${x - 1} ${bottom + 9} M ${left + 13} ${y + h + 1} L ${left + 29} ${y + h + 1}`} strokeWidth={1.6} strokeOpacity={0.8} />
          <path d={`M ${right + 1} ${bottom - 13} L ${right + 1} ${bottom + 5}`} strokeWidth={1.3} strokeOpacity={0.6} />
        </g>

        {/* ===== prism dispersion glints (tiny multi-hue ticks) ===== */}
        <g strokeWidth={1.2} strokeOpacity={0.85}>
          <path d={`M ${cx - 60} ${y - 2} l 4 0`} stroke="#ff7ab0" />
          <path d={`M ${cx - 55} ${y - 2} l 4 0`} stroke="#8ef0c0" />
          <path d={`M ${cx - 50} ${y - 2} l 4 0`} stroke="#7ab8ff" />
        </g>
        <g strokeWidth={1.1} strokeOpacity={0.8}>
          <path d={`M ${cx + 38} ${y + h + 2} l 3 0`} stroke="#ffd27a" />
          <path d={`M ${cx + 42} ${y + h + 2} l 3 0`} stroke="#c79bff" />
        </g>

        {/* ===== LEFT SPINE column (left bleed + card x 0..12) ===== */}
        <g>
          {/* spine separator hairline (just inside card edge) */}
          <line x1={left + 12} y1={top + 25} x2={left + 12} y2={bottom - 19} stroke={GREY} strokeWidth={0.5} strokeOpacity={0.35} />
          {/* DISC label box near top */}
          <rect x={x - 10} y={top + 41} width={19} height={10} rx={2.5} fill="none" stroke={CLEAR} strokeWidth={0.7} strokeOpacity={0.75} />
          <text x={x - 7.5} y={top + 48.5} fontSize={6} fill={CLEAR} letterSpacing={0.5} opacity={0.95}>DISC</text>
          {/* vertical DIGITAL DISC */}
          <text x={x - 2} y={top + 59} fontSize={5.5} fill={TEXT} letterSpacing={1.6} opacity={0.8} transform={`rotate(90 ${x - 2} ${top + 59})`}>DIGITAL DISC</text>
          {/* tiny globe */}
          <g opacity={0.8}>
            <circle cx={x - 2} cy={top + 105} r={4} fill="none" stroke={GREY} strokeWidth={0.6} />
            <line x1={x - 6} y1={top + 105} x2={x + 2} y2={top + 105} stroke={GREY} strokeWidth={0.5} />
            <path d={`M ${x - 2} ${top + 101} C ${x - 5} ${top + 103} ${x - 5} ${top + 107} ${x - 2} ${top + 109} M ${x - 2} ${top + 101} C ${x + 1} ${top + 103} ${x + 1} ${top + 107} ${x - 2} ${top + 109}`} fill="none" stroke={GREY} strokeWidth={0.5} />
          </g>
          {/* DIGITAL SOUND vertical */}
          <text x={x - 2} y={top + 117} fontSize={5} fill={TEXT} letterSpacing={1.2} opacity={0.75} transform={`rotate(90 ${x - 2} ${top + 117})`}>DIGITAL SOUND</text>
          {/* dot row */}
          <g fill={GREY} opacity={0.7}>
            <circle cx={x - 6} cy={top + 157} r={0.9} />
            <circle cx={x - 2} cy={top + 157} r={0.9} />
            <circle cx={x + 2} cy={top + 157} r={0.9} />
          </g>
          {/* hairlines */}
          <line x1={x - 9} y1={top + 163} x2={x + 8} y2={top + 163} stroke={GREY} strokeWidth={0.5} strokeOpacity={0.5} />
          <line x1={x - 9} y1={top + 166} x2={x + 3} y2={top + 166} stroke={GREY} strokeWidth={0.5} strokeOpacity={0.4} />
          {/* D chip */}
          <rect x={x - 9} y={top + 171} width={9} height={9} rx={2} fill="none" stroke={CLEAR} strokeWidth={0.6} opacity={0.7} />
          <text x={x - 6.6} y={top + 177.5} fontSize={6} fill={CLEAR} opacity={0.95}>D</text>
          {/* INSERT THIS END + downward triangle */}
          <text x={x - 2} y={top + 187} fontSize={4.5} fill={TEXT} letterSpacing={0.5} opacity={0.7} transform={`rotate(90 ${x - 2} ${top + 187})`}>INSERT THIS END</text>
          <path d={`M ${x - 4} ${y + h + 1} L ${x + 2} ${y + h + 1} L ${x - 1} ${y + h + 6} Z`} fill={TEXT} opacity={0.7} />
        </g>

        {/* ===== RIGHT iridescent disc peeking from behind the right edge ===== */}
        <g>
          <circle cx={dcx} cy={dcy} r={dr} fill={`url(#${g}iri)`} opacity={0.7} />
          <circle cx={dcx} cy={dcy} r={dr} fill="none" stroke="#ffffff" strokeWidth={0.7} strokeOpacity={0.6} />
          <circle cx={dcx} cy={dcy} r={dr * 0.8} fill="none" stroke="#ffffff" strokeWidth={0.5} strokeOpacity={0.45} />
          <circle cx={dcx} cy={dcy} r={dr * 0.56} fill="none" stroke="#ffffff" strokeWidth={0.4} strokeOpacity={0.35} />
          {/* specular sheen arc on disc */}
          <path d={`M ${dcx - dr * 0.8} ${dcy - dr * 0.44} A ${dr} ${dr} 0 0 1 ${dcx - dr * 0.56} ${dcy - dr * 0.64}`} fill="none" stroke="#ffffff" strokeWidth={1.6} strokeOpacity={0.55} />
          {/* metallic concentric hub */}
          <circle cx={dcx} cy={dcy} r={dr * 0.3} fill={`url(#${g}hub)`} opacity={0.9} />
          <circle cx={dcx} cy={dcy} r={dr * 0.3} fill="none" stroke={CLEAR} strokeWidth={0.5} opacity={0.6} />
          <circle cx={dcx} cy={dcy} r={dr * 0.16} fill={INK} />
          <circle cx={dcx} cy={dcy} r={dr * 0.16} fill="none" stroke={GREY} strokeWidth={0.7} opacity={0.75} />
        </g>

        {/* spec sticker near the disc, upper-right (in bleed / right edge) */}
        <g>
          <text x={right - 33} y={y - 2} fontSize={9} fontWeight="700" fill={CLEAR} opacity={0.9}>80</text>
          <text x={right - 21} y={y - 2} fontSize={5.5} fill={TEXT} opacity={0.7}>/ minutes</text>
          <text x={right - 33} y={y + 7} fontSize={4.5} fill={TEXT} opacity={0.65}>RW 74 / CORDLESS</text>
          <text x={right - 33} y={y + 13} fontSize={4.5} fill={TEXT} opacity={0.65}>RECORDING / SHOCK</text>
          <text x={right - 33} y={y + 19} fontSize={4.5} fill={TEXT} opacity={0.65}>ABSORBING MECHANISM</text>
        </g>

        {/* ===== CORNER registration "+" marks (inside each corner) ===== */}
        <g stroke={CLEAR} strokeWidth={0.7} strokeOpacity={0.6}>
          <path d={`M ${left + 16} ${top + 16} l 6 0 M ${left + 19} ${top + 13} l 0 6`} />
          <path d={`M ${right - 22} ${top + 16} l 6 0 M ${right - 19} ${top + 13} l 0 6`} />
          <path d={`M ${left + 16} ${bottom - 16} l 6 0 M ${left + 19} ${bottom - 19} l 0 6`} />
          <path d={`M ${right - 22} ${bottom - 16} l 6 0 M ${right - 19} ${bottom - 19} l 0 6`} />
        </g>

        {/* ===== BOTTOM-CENTER "OPEN" + downward triangle (in bottom bleed) ===== */}
        <g>
          <text x={cx - 14} y={y + h + 9} fontSize={6} fill={TEXT} letterSpacing={2} opacity={0.8}>OPEN</text>
          <path d={`M ${cx + 10} ${y + h + 4} L ${cx + 16} ${y + h + 4} L ${cx + 13} ${y + h + 9} Z`} fill={TEXT} opacity={0.8} />
        </g>

        {/* ===== CENTER faint dotted J-card insert rect (very low opacity) ===== */}
        <rect x={x + 26} y={y + 28} width={w - 52} height={h - 56} rx={6} fill="none" stroke={GREY} strokeWidth={0.5} strokeOpacity={0.12} strokeDasharray="2 3" />
      </svg>
    </div>
  );
}
